/**
 * colourPalette.js — Centralised colour definitions for Portfolio Reports.
 *
 * Every hardcoded colour in BlockRenderer / chart components is mapped to a
 * semantic key here.  The editor's "Colours" tab lets users override any key;
 * resolveColours() merges overrides on top of the correct defaults (Drummond
 * or branded).
 */

// ── Drummond default palette ──
export const DEFAULT_COLOUR_PALETTE = {
  // Tables
  table_header_primary:   '#011E41',
  table_header_secondary: '#A6C6C1',
  table_row_band:         '#F2F2F2',

  // Charts — primary / secondary series
  chart_primary:   '#337B91',
  chart_secondary: '#BBCBD7',

  // Performance chart
  perf_portfolio_line: '#072040',
  perf_benchmark_line: '#599BE3',
  perf_cash_line:      '#f0a500',

  // Historical allocation (asset class group scheme)
  hist_australian_equities:    '#011E41',
  hist_international_equities: '#5E819C',
  hist_real_estate:            '#A6C6C1',
  hist_infrastructure:         '#337B91',
  hist_global_alpha:           '#BBCBD7',
  hist_corporate_debt:         '#AA8090',
  hist_government_bonds:       '#F3BD48',
  hist_cash:                   '#EFE6D5',
  hist_commodities:            '#4B3219',
  hist_private_equity:         '#6C757D',
  hist_private_debt:           '#874564',
  hist_private_infrastructure: '#12706C',

  // Growth vs Defensive
  growth_bar:    '#072040',
  defensive_bar: '#599BE3',

  // Drawdown
  drawdown_line: '#f0a500',

  // Risk-Return scatter
  scatter_portfolio: '#072040',
  scatter_peer:      '#f0a500',

  // Sector / Allocation bars
  sector_actual: '#072040',
  sector_saa:    '#599BE3',

  // Waterfall
  waterfall_positive: '#91CF9B',
  waterfall_negative: '#f0a500',

  // Active tilts / Allocation vs SAA
  tilt_positive_fill: '#337B91',
  tilt_negative_fill: '#ffffff',
  tilt_border:        '#337B91',

  // Credit exposure
  credit_bar: '#337B91',

  // Title & text
  title_accent:    '#599BE3',
  subtitle_colour: '#5E819C',

  // Contribution tables
  contribution_positive: '#91CF9B',
  contribution_negative: '#dc3545',
};

// ── Branded-client defaults (overlay branding onto Drummond palette) ──
export const getBrandedDefaults = (branding) => ({
  ...DEFAULT_COLOUR_PALETTE,
  table_header_primary:  branding.primary_color  || DEFAULT_COLOUR_PALETTE.table_header_primary,
  table_header_secondary: branding.secondary_color || DEFAULT_COLOUR_PALETTE.table_header_secondary,
  chart_primary:   branding.primary_color  || DEFAULT_COLOUR_PALETTE.chart_primary,
  chart_secondary: branding.secondary_color || DEFAULT_COLOUR_PALETTE.chart_secondary,
  title_accent:    branding.secondary_color || DEFAULT_COLOUR_PALETTE.title_accent,
  subtitle_colour: branding.secondary_color || DEFAULT_COLOUR_PALETTE.subtitle_colour,

  // Charts that should follow brand primary
  credit_bar:          branding.primary_color  || DEFAULT_COLOUR_PALETTE.credit_bar,
  tilt_positive_fill:  branding.primary_color  || DEFAULT_COLOUR_PALETTE.tilt_positive_fill,
  tilt_border:         branding.primary_color  || DEFAULT_COLOUR_PALETTE.tilt_border,
  sector_actual:       branding.primary_color  || DEFAULT_COLOUR_PALETTE.sector_actual,
  growth_bar:          branding.primary_color  || DEFAULT_COLOUR_PALETTE.growth_bar,
  scatter_portfolio:   branding.primary_color  || DEFAULT_COLOUR_PALETTE.scatter_portfolio,

  // Charts that should follow brand secondary
  sector_saa:          branding.secondary_color || DEFAULT_COLOUR_PALETTE.sector_saa,
  defensive_bar:       branding.secondary_color || DEFAULT_COLOUR_PALETTE.defensive_bar,
  scatter_peer:        branding.secondary_color || DEFAULT_COLOUR_PALETTE.scatter_peer,
});

// ── Resolver: returns the final merged palette for a given report ──
export const resolveColours = (data) => {
  const defaults = data.branding
    ? getBrandedDefaults(data.branding)
    : DEFAULT_COLOUR_PALETTE;
  const overrides = data.content?.colour_overrides || {};
  return { ...defaults, ...overrides };
};

// ── Human-readable labels for each key ──
const KEY_LABELS = {
  table_header_primary:   'Header Primary',
  table_header_secondary: 'Header Secondary',
  table_row_band:         'Row Banding',
  chart_primary:          'Primary Series',
  chart_secondary:        'Secondary Series',
  perf_portfolio_line:    'Portfolio Line',
  perf_benchmark_line:    'Benchmark Line',
  perf_cash_line:         'Cash Line',
  hist_australian_equities:    'Australian Equities',
  hist_international_equities: 'International Equities',
  hist_real_estate:            'Real Estate',
  hist_infrastructure:         'Infrastructure',
  hist_global_alpha:           'Global Alpha',
  hist_corporate_debt:         'Corporate Debt',
  hist_government_bonds:       'Government Bonds',
  hist_cash:                   'Cash',
  hist_commodities:            'Commodities',
  hist_private_equity:         'Private Equity',
  hist_private_debt:           'Private Debt',
  hist_private_infrastructure: 'Private Infrastructure',
  growth_bar:       'Growth',
  defensive_bar:    'Defensive',
  drawdown_line:    'Drawdown Line',
  scatter_portfolio: 'Portfolio Point',
  scatter_peer:      'Peer Point',
  sector_actual:     'Actual',
  sector_saa:        'SAA',
  waterfall_positive: 'Positive',
  waterfall_negative: 'Negative',
  tilt_positive_fill: 'Positive Fill',
  tilt_negative_fill: 'Negative Fill',
  tilt_border:        'Border',
  credit_bar:         'Bar Colour',
  title_accent:       'Title Accent',
  subtitle_colour:    'Subtitle',
  contribution_positive: 'Positive',
  contribution_negative: 'Negative',
};

export const getKeyLabel = (key) => KEY_LABELS[key] || key.replace(/_/g, ' ');

// ── Groups for the editor UI ──
export const COLOUR_GROUPS = [
  { label: 'Tables', keys: ['table_header_primary', 'table_header_secondary', 'table_row_band'] },
  { label: 'Title & Text', keys: ['title_accent', 'subtitle_colour'] },
  { label: 'Charts — Primary Series', keys: ['chart_primary', 'chart_secondary'] },
  { label: 'Performance Chart', keys: ['perf_portfolio_line', 'perf_benchmark_line', 'perf_cash_line'] },
  {
    label: 'Historical Allocation',
    keys: [
      'hist_australian_equities', 'hist_international_equities', 'hist_real_estate',
      'hist_infrastructure', 'hist_global_alpha', 'hist_corporate_debt',
      'hist_government_bonds', 'hist_cash', 'hist_commodities', 'hist_private_equity',
      'hist_private_debt', 'hist_private_infrastructure',
    ],
  },
  { label: 'Growth vs Defensive', keys: ['growth_bar', 'defensive_bar'] },
  { label: 'Drawdown', keys: ['drawdown_line'] },
  { label: 'Risk-Return', keys: ['scatter_portfolio', 'scatter_peer'] },
  { label: 'Sector Exposure', keys: ['sector_actual', 'sector_saa'] },
  { label: 'Waterfall', keys: ['waterfall_positive', 'waterfall_negative'] },
  { label: 'Active Tilts', keys: ['tilt_positive_fill', 'tilt_negative_fill', 'tilt_border'] },
  { label: 'Credit Exposure', keys: ['credit_bar'] },
  { label: 'Contribution Tables', keys: ['contribution_positive', 'contribution_negative'] },
];
